import { useState, useEffect, type ChangeEvent, type FormEvent } from 'react'
import { X, ChevronLeft } from 'lucide-react'
import { adminApi } from '@/utils/api'
import { useAuth } from '@/context/AuthContext'

interface EditDetailsModalProps {
  isOpen: boolean
  onClose: () => void
  userData: {
    fullName: string
    firstName?: string
    lastName?: string
    gender?: string
    email?: string
    phone?: string
    state?: string
    country?: string
    location?: string
    tribe?: string
    genotype?: string
    dateOfBirth?: string
  }
  userId?: string
}

export default function EditDetailsModal({ isOpen, onClose, userData, userId }: EditDetailsModalProps) {
  const { refreshProfile } = useAuth()
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    gender: '',
    email: '',
    phone: '',
    country: '',
    state: '',
    location: '',
    tribe: '',
    genotype: '',
    dateOfBirth: '',
  })
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [success, setSuccess] = useState<string | null>(null)

  useEffect(() => {
    if (isOpen) {
      // Split full name into first and last name
      const nameParts = (userData.fullName || '').split(' ')
      setFormData({
        firstName: userData.firstName || nameParts[0] || '',
        lastName: userData.lastName || nameParts.slice(1).join(' ') || '',
        gender: userData.gender || '',
        email: userData.email || '',
        phone: userData.phone || '',
        country: userData.country || '',
        state: userData.state || '',
        location: userData.location || '',
        tribe: userData.tribe || '',
        genotype: userData.genotype || '',
        dateOfBirth: userData.dateOfBirth ? (userData.dateOfBirth.split('T')[0] ?? '') : '',
      })
      setError(null)
      setSuccess(null)
    }
  }, [isOpen, userData])

  const handleChange = (e: ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    })
  }

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault()
    if (!userId) {
      setError('Missing user id for update.')
      return
    }

    setSaving(true)
    setError(null)
    setSuccess(null)

    try {
      const payload: Record<string, string> = {}
      if (formData.firstName) payload.firstName = formData.firstName.trim()
      if (formData.lastName) payload.lastName = formData.lastName.trim()
      if (formData.gender) payload.gender = formData.gender.trim()
      if (formData.country) payload.country = formData.country.trim()
      if (formData.state) payload.state = formData.state.trim()
      if (formData.location) payload.location = formData.location.trim()
      if (formData.tribe) payload.tribe = formData.tribe.trim()
      if (formData.genotype) payload.genotype = formData.genotype.trim()
      if (formData.dateOfBirth) payload.dateOfBirth = formData.dateOfBirth

      await adminApi.updateUser(userId, payload)
      await refreshProfile()
      setSuccess('Profile updated.')
    } catch (err) {
      setError((err as Error)?.message || 'Unable to update profile')
    } finally {
      setSaving(false)
    }
  }

  if (!isOpen) return null

  return (
    <>
      {/* Backdrop */}
      <div
        className='fixed inset-0 bg-black/50 z-40 transition-opacity'
        onClick={onClose}
      />

      {/* Right Side Modal */}
      <div className='fixed inset-y-0 right-0 z-50 w-full sm:w-[480px] md:w-[520px] lg:w-[560px] pointer-events-none'>
        <div className='bg-white h-full shadow-2xl pointer-events-auto flex flex-col animate-slide-in-right'>
          {/* Header */}
          <div className='flex items-center justify-between px-6 py-4 border-b border-gray-200'>
            <div className='flex items-center gap-3'>
              <button
                onClick={onClose}
                className='p-1 hover:bg-gray-100 rounded-lg transition-colors md:hidden'
              >
                <ChevronLeft className='h-5 w-5 text-gray-600' />
              </button>
              <h2 className='text-lg md:text-xl font-bold text-gray-900'>Edit Details</h2>
            </div>
            <button
              onClick={onClose}
              className='hidden md:block p-1 hover:bg-gray-100 rounded-lg transition-colors'
            >
              <X className='h-5 w-5 text-gray-600' />
            </button>
          </div>

          {/* Form */}
          <form
            id='edit-details-form'
            onSubmit={handleSubmit}
            className='flex-1 overflow-y-auto px-6 py-6'
          >
            <p className='text-xs text-gray-600 mb-3 text-start'>
              Update your profile details. Email and phone remain managed by support to avoid conflicts.
            </p>
            <div className='space-y-4'>
              {/* First Name */}
              <div>
                <label className='block text-sm font-medium text-gray-700 mb-2 text-start'>
                  First Name
                </label>
                <input
                  type='text'
                  name='firstName'
                  value={formData.firstName}
                  onChange={handleChange}
                  placeholder='Success'
                  className='w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent text-sm'
                  required
                />
              </div>

              {/* Last Name */}
              <div>
                <label className='block text-sm font-medium text-gray-700 mb-2 text-start'>
                  Last Name
                </label>
                <input
                  type='text'
                  name='lastName'
                  value={formData.lastName}
                  onChange={handleChange}
                  placeholder='Olatunde'
                  className='w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent text-sm'
                  required
                />
              </div>

              {/* Gender */}
              <div>
                <label className='block text-sm font-medium text-gray-700 mb-2 text-start'>Gender</label>
                <select
                  name='gender'
                  value={formData.gender}
                  onChange={handleChange}
                  className='w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent text-sm appearance-none bg-white'
                >
                  <option value=''>Select gender</option>
                  <option value='male'>Male</option>
                  <option value='female'>Female</option>
                  <option value='other'>Other</option>
                </select>
              </div>

              {/* Email */}
              <div>
                <label className='block text-sm font-medium text-gray-700 mb-2 text-start'>Email</label>
                <input
                  type='email'
                  name='email'
                  value={formData.email}
                  onChange={handleChange}
                  placeholder='johndoe@gmail.com'
                  disabled
                  className='w-full px-4 py-2.5 border border-gray-200 rounded-lg bg-gray-50 text-gray-500 text-sm'
                />
              </div>

              {/* Phone Number */}
              <div>
                <label className='block text-sm font-medium text-gray-700 mb-2 text-start'>
                  Phone Number
                </label>
                <input
                  type='tel'
                  name='phone'
                  value={formData.phone}
                  onChange={handleChange}
                  placeholder='+2348012345678'
                  disabled
                  className='w-full px-4 py-2.5 border border-gray-200 rounded-lg bg-gray-50 text-gray-500 text-sm'
                />
              </div>

              {/* Date of Birth */}
              <div>
                <label className='block text-sm font-medium text-gray-700 mb-2 text-start'>
                  Date of Birth
                </label>
                <input
                  type='date'
                  name='dateOfBirth'
                  value={formData.dateOfBirth}
                  onChange={handleChange}
                  className='w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent text-sm'
                />
              </div>

              {/* Country */}
              <div>
                <label className='block text-sm font-medium text-gray-700 mb-2 text-start'>Country</label>
                <input
                  type='text'
                  name='country'
                  value={formData.country}
                  onChange={handleChange}
                  placeholder='************************'
                  className='w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent text-sm'
                />
              </div>

              {/* State */}
              <div>
                <label className='block text-sm font-medium text-gray-700 mb-2 text-start'>State</label>
                <input
                  type='text'
                  name='state'
                  value={formData.state}
                  onChange={handleChange}
                  placeholder='************************'
                  className='w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent text-sm'
                />
              </div>

              {/* Location */}
              <div>
                <label className='block text-sm font-medium text-gray-700 mb-2 text-start'>Location</label>
                <input
                  type='text'
                  name='location'
                  value={formData.location}
                  onChange={handleChange}
                  placeholder='City, Area'
                  className='w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent text-sm'
                />
              </div>

              {/* Tribe */}
              <div>
                <label className='block text-sm font-medium text-gray-700 mb-2 text-start'>Tribe</label>
                <input
                  type='text'
                  name='tribe'
                  value={formData.tribe}
                  onChange={handleChange}
                  placeholder='Your tribe'
                  className='w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent text-sm'
                />
              </div>

              {/* Genotype */}
              <div>
                <label className='block text-sm font-medium text-gray-700 mb-2 text-start'>Genotype</label>
                <input
                  type='text'
                  name='genotype'
                  value={formData.genotype}
                  onChange={handleChange}
                  placeholder='e.g. AA, AS'
                  className='w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent text-sm'
                />
              </div>

              {(error || success) && (
                <div
                  className={`text-sm rounded-lg px-4 py-3 ${
                    error ? 'bg-red-50 text-red-700' : 'bg-green-50 text-green-700'
                  }`}
                >
                  {error || success}
                </div>
              )}
            </div>
          </form>

          {/* Footer with Save Button */}
          <div className='px-6 py-4 border-t border-gray-200'>
            <button
              type='submit'
              form='edit-details-form'
              disabled={saving}
              className='w-full py-3 bg-gray-900 text-white rounded-lg font-semibold text-sm hover:bg-gray-800 transition-colors disabled:opacity-60 disabled:cursor-not-allowed'
            >
              {saving ? 'Saving...' : 'Save'}
            </button>
          </div>
        </div>
      </div>
    </>
  )
}
